import React, { useState, useRef } from "react";
import "./SygnetInvoice.css";

const App = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");

  const handleLogin = (e) => {
    e.preventDefault();
    if (username === "sygnet" && password === "sygnet") {
      setIsLoggedIn(true);
      setLoginError("");
    } else {
      setLoginError(
        "Invalid credentials."
      );
    }
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-100">
        <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
          {/* Centered Logo */}
          <div className="flex justify-center mb-6">
            <img
              src="/assets/image/SYG.png"
              alt="Company Logo"
              className="w-48 h-auto object-contain"
            />
          </div>

          <h1 className="text-2xl font-bold mb-6 text-center">
            Certificate Portal Login
          </h1>

          {loginError && (
            <div className="mb-4 p-2 bg-red-100 text-red-700 rounded text-sm">
              {loginError}
            </div>
          )}

          <form onSubmit={handleLogin}>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter username"
                required
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium mb-1">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter password"
                required
              />
            </div>

            <button
              type="submit"
              className="w-full bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600 transition"
            >
              Login
            </button>
          </form>

          {/* Copyright Notice */}
          <div className="mt-8 text-center text-sm text-gray-500">
            <a
              href="https://inittechnology.co/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline"
            >
              Copyright © 2025 by Init Technology.
            </a>
          </div>
        </div>
      </div>
    );
  }

  return <CertificateForm onLogout={() => setIsLoggedIn(false)} />;
};

const CertificateForm = ({ onLogout }) => {
  const [photo, setPhoto] = useState(null);
  const [labels, setLabels] = useState({
    pcode: "P.CODE",
    goldKt: "RS GOLD KT",
    goldWt: "GOLD Wt",
    netWt: "Net Wt",
    extra: "Extra",
    diamond: "Diamond",
    quality: "Quality",
    dPcs: "D.Pcs",
    dCrt: "D.Crt",
    extra2: "Extra",
  });
  const [editingLabel, setEditingLabel] = useState(null);
  const [formData, setFormData] = useState({
    pcode: "",
    goldKt: "",
    goldWt: "",
    netWt: "",
    extra: "",
    diamond: "",
    quality: "",
    dPcs: "",
    dCrt: "",
    extra2: "",
  });

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setPhoto(URL.createObjectURL(file));
    }
  };

  const handleLabelDoubleClick = (field) => {
    setEditingLabel(field);
  };

  const handleLabelChange = (e, field) => {
    setLabels((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleLabelBlur = () => {
    setEditingLabel(null);
  };

  const handleInputChange = (e, field) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const InputField = ({ field, placeholder }) => (
    <div className="flex flex-col">
      {editingLabel === field ? (
        <input
          type="text"
          value={labels[field]}
          onChange={(e) => handleLabelChange(e, field)}
          onBlur={handleLabelBlur}
          className="font-bold text-xl mb-1 p-1 border-b-2 focus:outline-none print:text-[4pt] print:mb-0 print:p-0"
          autoFocus
        />
      ) : (
        <label
          htmlFor={field}
          className="font-bold text-xl mb-1 cursor-text print:text-[4pt] print:mb-0 print:font-bold"
          onDoubleClick={() => handleLabelDoubleClick(field)}
        >
          {labels[field]}
        </label>
      )}
      <input
        id={field}
        type="text"
        placeholder={placeholder}
        value={formData[field]}
        onChange={(e) => handleInputChange(e, field)}
        className="p-3 rounded text-xl h-14 focus:ring-2 focus:ring-blue-200 focus:outline-none 
                   print:text-[3.5pt] print:p-[0.003in] print:h-[0.07in] print:min-h-[0.07in] print:border-[0.2pt]"
      />
    </div>
  );

  return (
    <div className="w-full min-h-screen flex flex-col bg-white p-6 ">
      {/* Action buttons container */}
      <div className="flex justify-end mb-4 print:hidden gap-2">
        <button
          onClick={() => window.print()}
          className="mt-4 px-4 py-2 border rounded border-[#d4985b] text-[#d4985b] hover:bg-[#d4985b] hover:text-white transition transform duration-300 print:hidden"
        >
          Print Certificate
        </button>


        <button
          onClick={onLogout}
          className="mt-4 px-4 py-2 border border-[#8a796c] text-[#8a796c] hover:bg-[#8a796c] hover:text-white transition transform duration-300  rounded print:hidden"
        >
          Logout
        </button>

      </div>

      {/* Certificate content with print styling */}
      <div className="certificate-container w-full max-w-5xl mx-auto p-6 bg-white shadow-md rounded-md
                print:w-[3.5in] print:h-[2in] print:p-[0.05in] print:m-0 print:box-border print:overflow-hidden 
                print:border-none print:shadow-none print:rounded-none print:leading-[1.1] print:tracking-tight">


        {/* Header: Logos */}
        <div className="flex justify-between items-center mb-6 print:mb-[0.002in]">
          {/* Left Logo */}
          <img
            src="/assets/image/download.webp"
            alt="Left Logo"
            className="h-17 w-auto object-contain print:h-[0.5in]"
          />

          {/* Right Side */}
          <div className="text-right">
            <p className="text-md font-semibold mb-1 print:text-[5pt]">On Behalf of</p>
            <img
              src="/assets/image/SYG.png"
              alt="Right Logo"
              className="h-14 object-contain print:h-[0.35in]"
            />
          </div>
        </div>

        {/* Title */}
        <div className="text-center text-2xl font-bold mb-4 print:text-[6pt] print:mb-[0.003in]">
          CERTIFICATE OF AUTHENTICITY
        </div>

        {/* Form and Photo */}
        <div className="flex gap-6 print:gap-[0.003in]">
          {/* Form */}
          <div className="flex-1 grid grid-cols-4 gap-4 text-sm print:gap-[0.02in] print:text-[4.5pt]">

            {/* Example Input */}
            {[
              { id: "pcode", label: "Product Code" },
              { id: "goldKt", label: "Gold Karat" },
              { id: "goldWt", label: "Gold Weight" },
              { id: "netWt", label: "Net Weight" },
              { id: "diamond", label: "Diamond" },
              { id: "quality", label: "Quality" },
              { id: "dPcs", label: "Diamond Pcs" },
              { id: "dCrt", label: "Diamond Crt" },
            ].map(({ id, label }) => (
              <div key={id} className="flex flex-col print:gap-0 print:leading-tight">
                <label htmlFor={id} className="font-semibold print:text-[6pt] print:mb-0">
                  {label}
                </label>
                <input
                  id={id}
                  type="text"
                  className="border rounded p-2 text-black 
      print:border-none print:p-0 print:m-0 print:text-[6pt] 
      print:bg-transparent print:h-[0.12in] print:leading-none print:inline-block"
                />
              </div>

            ))}
          </div>

          {/* Photo Upload */}
          <div className="w-[10rem] h-[10rem] border border-dashed flex items-center justify-center 
                text-xs text-gray-500 rounded bg-gray-100 
                print:w-[0.5in] print:h-[0.5in] print:border-none print:p-0 print:rounded-none">

            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              id="photoUpload"
              className="hidden"
            />
            <label htmlFor="photoUpload" className="cursor-pointer w-full h-full flex items-center justify-center">
              {photo ? (
                <img src={photo} alt="Uploaded" className="w-full h-full object-contain print:object-contain" />

              ) : (
                "Add Photo"
              )}
            </label>
          </div>
        </div>

        {/* Terms & Conditions */}
        <div className="mt-6 print:mt-2 print:text-[3pt] print:leading-tight">
          <p className="font-semibold mb-2 print:mb-[0.05in]">TERMS AND CONDITIONS</p>
          <ul className="list-disc ml-4 space-y-1 print:ml-[0.2in] print:space-y-[0.02in]">
            <li>Color grading is based on Face-up Method.</li>
            <li>Average grade shown for all stones.</li>
            <li>Carat weight as per manufacturer submission.</li>
            <li>No liability for mishaps or misrepresentation.</li>
            <li>Claims are not the company’s responsibility.</li>
          </ul>
        </div>

      </div>
    </div>
  );
};

export default App;